import { PageLayout } from '@/components/feature/PageLayout';
import { useFadeUp } from '@/hooks/useFadeUp';
import { Zap, Smartphone, Wifi, Shield, Check } from 'lucide-react';

const seo = {
  title: 'Motorisation de volet roulant à Charleroi — Volet manuel en électrique',
  description: 'Transformez votre volet roulant manuel en volet électrique à Charleroi. Installation moteur tubulaire Somfy, Simu, Nice. Pilotage par télécommande ou smartphone. Devis gratuit. Garantie 10 ans.',
  keywords: 'motorisation volet roulant Charleroi, volet manuel en électrique, moteur tubulaire Somfy, motorisation volet Hainaut, télécommande volet roulant, domotique volet',
  canonicalUrl: '/motorisation-volet-charleroi/',
};

function Hero() {
  return (
    <section className="relative min-h-[350px] md:min-h-[420px] flex items-center overflow-hidden">
      <img
        src="/images/hero-motorisation.webp"
        alt="Motorisation de volet roulant à Charleroi"
        className="absolute inset-0 w-full h-full object-cover object-top"
        loading="eager"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-primary-500/85 to-secondary-500/70" />
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
        <div className="max-w-2xl">
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-white leading-tight">
            Motorisation de volet roulant à Charleroi
          </h1>
          <p className="mt-4 text-base md:text-lg text-white/85">
            Transformez votre volet manuel en volet électrique — confort, sécurité et pilotage intelligent
          </p>
        </div>
      </div>
    </section>
  );
}

export default function MotorisationPage() {
  const ref1 = useFadeUp();
  const ref2 = useFadeUp();
  const ref3 = useFadeUp();
  const ref4 = useFadeUp();

  return (
    <PageLayout seo={seo}>
      <Hero />

      {/* Introduction */}
      <section className="py-14 md:py-20 bg-background-50">
        <div ref={ref1} className="fade-up max-w-4xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-700 mb-5">
                Passez du volet manuel au volet électrique
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                <p>
                  Vous en avez assez de tourner la manivelle ou de tirer sur la sangle chaque matin et chaque soir ? La <strong>motorisation de votre volet roulant</strong> est la solution idéale pour gagner en confort au quotidien.
                </p>
                <p>
                  Nous installons un <strong>moteur tubulaire</strong> directement dans le tube d'enroulement de votre volet existant. L'opération est rapide, propre, et ne nécessite pas de remplacer votre volet. Votre tablier et votre coffre restent en place.
                </p>
                <p>
                  Une fois motorisé, vous pilotez votre volet par <strong>télécommande</strong>, par <strong>interrupteur mural</strong>, ou même depuis votre <strong>smartphone</strong> si vous optez pour une solution connectée.
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden h-80 lg:h-96">
              <img
                src="/images/motorisation-moteur.webp"
                alt="Moteur tubulaire et télécommande pour volet roulant"
                title="Motorisation volet roulant Charleroi"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section className="py-14 md:py-20 bg-background-100">
        <div ref={ref2} className="fade-up max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="section-title text-center mb-10">Les avantages de la motorisation</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Zap, title: 'Confort absolu', desc: 'Un seul clic pour ouvrir ou fermer votre volet. Fini la manivelle ou la sangle à tirer.' },
              { icon: Smartphone, title: 'Pilotage intelligent', desc: 'Contrôlez vos volets depuis votre smartphone, où que vous soyez. Programmation horaire possible.' },
              { icon: Wifi, title: 'Domotique', desc: 'Intégrez vos volets à votre système domotique (Somfy TaHoma, Google Home, Amazon Alexa).' },
              { icon: Shield, title: 'Sécurité renforcée', desc: 'Simulez une présence en programmant l\'ouverture et la fermeture automatique de vos volets.' },
              { icon: Check, title: 'Installation propre', desc: 'Pas de gros travaux. Le moteur s\'installe dans le tube existant en quelques heures.' },
              { icon: Shield, title: 'Garantie 10 ans', desc: 'Toutes nos installations de motorisation sont garanties 10 ans, pièces et main-d\'œuvre.' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="card border border-background-200 text-center">
                  <div className="w-11 h-11 rounded-xl bg-accent-50 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-5 h-5 text-accent-500" />
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-700 text-sm mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Marques */}
      <section className="py-14 md:py-20 bg-background-50">
        <div ref={ref3} className="fade-up max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="section-title text-center mb-4">Motorisation toutes marques</h2>
          <p className="section-subtitle text-center mb-10">
            Nous travaillons avec les plus grandes marques de motorisation pour volets roulants
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {['Somfy', 'Simu', 'Nice', 'Bubendorff', 'Profalux', 'Hörmann', 'Velux', 'FAAC', 'Aprimatic', 'Selve'].map((marque) => (
              <div key={marque} className="flex items-center justify-center px-4 py-3 bg-white border border-background-200 rounded-lg text-sm font-medium text-foreground-600">
                {marque}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16 bg-primary-500" ref={ref4}>
        <div className="fade-up max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
            Prêt à motoriser votre volet roulant ?
          </h2>
          <p className="text-base text-white/80 mb-6">
            Demandez un devis gratuit pour la motorisation de votre volet roulant. Intervention à Charleroi et dans le Hainaut.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="tel:+3280020410" className="btn-primary text-base px-8 py-3.5 inline-flex items-center gap-2">
              0800 20 410
            </a>
            <Link to="/contact/" className="btn-secondary text-base px-8 py-3.5 inline-flex items-center gap-2 border-white/30 hover:bg-white/10">
              Demander un devis
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}